import { ref, watch } from "vue";
import L from "leaflet";
import type { Map as LeafletMap } from "leaflet";
import { API_BASE } from "@/config";

interface MockPoint {
  assetId: string;
  assetType: string;
  lat: number;
  lng: number;
  speed: number;
  heading: number | null;
  time: number; // unix seconds
}

function makeNumberedIcon(n: number): L.DivIcon {
  return L.divIcon({
    html: `<div style="width:20px;height:20px;background:#1565C0;border-radius:50%;
                       border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.6);
                       color:#fff;font-size:10px;font-weight:700;
                       display:flex;align-items:center;justify-content:center;">${n}</div>`,
    className: "",
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

export function useMockGps(
  getMap: () => LeafletMap | null,
  options: {
    onSaved: () => void;
    onMapClick?: () => void; // side effect before placing pending marker (e.g. resetHighlight)
    getVehiclePosition?: (
      assetId: string,
    ) => { lat: number; lng: number } | null;
  },
) {
  const mockMode = ref(false);
  const mockVehicleId = ref("");
  const mockSpeed = ref(20);
  const pendingPoint = ref<{ lat: number; lng: number } | null>(null);
  const points = ref<MockPoint[]>([]);
  const saving = ref(false);

  let pendingMarker: L.Marker | null = null;
  const numberedMarkers: L.Marker[] = []; // one per confirmed point, index matches points[]
  const lastPoints = new Map<string, { lat: number; lng: number }>();

  // ── helpers ───────────────────────────────────────────────────────────────

  /** Seed lastPoints from live GPS data if we don't already have a point for this session. */
  function seedPosition(assetId: string) {
    if (lastPoints.has(assetId)) return;
    const pos = options.getVehiclePosition?.(assetId);
    if (pos) lastPoints.set(assetId, pos);
  }

  // When the selected vehicle changes while mock mode is active, seed its position.
  watch(mockVehicleId, (assetId) => {
    if (mockMode.value) seedPosition(assetId);
  });

  function removeAllNumberedMarkers() {
    numberedMarkers.forEach((m) => m.remove());
    numberedMarkers.length = 0;
  }

  // ── mode toggle ───────────────────────────────────────────────────────────

  function toggleMockMode() {
    const map = getMap();
    if (!mockMode.value) {
      mockMode.value = true;
      seedPosition(mockVehicleId.value);
      map?.on("click", mockClickHandler);
    } else {
      cancelPending();
      mockMode.value = false;
      map?.off("click", mockClickHandler);
    }
  }

  // ── map click ─────────────────────────────────────────────────────────────

  function mockClickHandler(e: L.LeafletMouseEvent) {
    const map = getMap();
    if (!map) return;
    options.onMapClick?.();
    map.closePopup();

    pendingMarker?.remove();
    pendingMarker = L.marker([e.latlng.lat, e.latlng.lng], {
      icon: L.divIcon({
        html: `<div style="width:14px;height:14px;background:#E65100;border-radius:50%;
                           border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.6)"></div>`,
        className: "",
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      }),
      zIndexOffset: 1000,
    }).addTo(map);
    pendingPoint.value = { lat: e.latlng.lat, lng: e.latlng.lng };
  }

  // ── confirm pending → add to in-memory list ───────────────────────────────

  function addPendingPoint() {
    if (!pendingPoint.value) return;
    const map = getMap();
    const { lat, lng } = pendingPoint.value;
    const assetId = mockVehicleId.value;
    const prev = lastPoints.get(assetId);

    // Geographic heading from the vector formed by the two most recent points.
    // e.latlng from Leaflet always returns geographic (WGS84) coordinates regardless
    // of map bearing, so atan2 gives true geographic heading — no bearing offset needed.
    // cos(midLat) corrects longitude compression at non-equatorial latitudes.
    let heading: number | null = null;
    if (prev) {
      const midLat = (lat + prev.lat) / 2;
      const dLng = (lng - prev.lng) * Math.cos((midLat * Math.PI) / 180);
      const dLat = lat - prev.lat;
      heading = ((Math.atan2(dLng, dLat) * 180) / Math.PI + 360) % 360;
    }
    lastPoints.set(assetId, { lat, lng });

    points.value.push({
      assetId,
      assetType: "vehicle",
      lat,
      lng,
      speed: mockSpeed.value,
      heading,
      time: Math.floor(Date.now() / 1000),
    });

    // Replace the orange pending marker with a numbered blue marker.
    pendingMarker?.remove();
    pendingMarker = null;
    if (map) {
      const m = L.marker([lat, lng], {
        icon: makeNumberedIcon(points.value.length),
        zIndexOffset: 900,
      }).addTo(map);
      numberedMarkers.push(m);
    }

    pendingPoint.value = null;
  }

  // ── cancel pending ────────────────────────────────────────────────────────

  function cancelPending() {
    pendingMarker?.remove();
    pendingMarker = null;
    pendingPoint.value = null;
  }

  // ── clear in-memory list (does NOT touch the server) ─────────────────────

  function clearPendingList() {
    cancelPending();
    removeAllNumberedMarkers();
    points.value = [];
    lastPoints.clear();
  }

  // ── batch save to backend ─────────────────────────────────────────────────

  async function saveAll() {
    if (points.value.length === 0) return;
    saving.value = true;
    try {
      const body = points.value.map((p) => ({
        assetId: p.assetId,
        assetType: p.assetType,
        lat: p.lat,
        lng: p.lng,
        speed: p.speed,
        heading: p.heading,
        time: new Date(p.time * 1000).toISOString(),
      }));
      const res = await fetch(`${API_BASE}/api/gps-tracks/batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        removeAllNumberedMarkers();
        points.value = [];
        lastPoints.clear();
        options.onSaved();
      }
    } catch (err) {
      console.error("Batch save failed:", err);
    } finally {
      saving.value = false;
    }
  }

  // ── export JSON ───────────────────────────────────────────────────────────
  // Format matches test-gps-data-1.json: unix seconds, heading defaults to 0.

  function exportJson() {
    const data = points.value.map((p) => ({
      assetId: p.assetId,
      assetType: p.assetType,
      lat: p.lat,
      lng: p.lng,
      speed: p.speed,
      heading: p.heading ?? 0,
      time: p.time,
    }));
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mock-gps-${mockVehicleId.value}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── cleanup for onUnmounted ───────────────────────────────────────────────

  function cleanup() {
    if (mockMode.value) {
      cancelPending();
      getMap()?.off("click", mockClickHandler);
    }
    removeAllNumberedMarkers();
  }

  return {
    mockMode,
    mockVehicleId,
    mockSpeed,
    pendingPoint,
    points,
    saving,
    toggleMockMode,
    mockClickHandler,
    addPendingPoint,
    cancelPending,
    clearPendingList,
    saveAll,
    exportJson,
    cleanup,
  };
}
